import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Cookies } from "react-cookie";
import { saveAs } from "file-saver";
import CryptoJS from "crypto-js";
import toast from "react-hot-toast";
import moment from "moment";
import { Switch } from "antd";
import SkeletonTable from "../../components/table/SkeletonTable";
import columns from "../../columns/clients/column";
import View from "../../assets/View";
import Add from "../../assets/Add";
import Delete from "../../assets/Delete";
import Login from "../../assets/Login";
import ConfirmationModal from "../../components/modals/ConfirmationModal";
import AddClientModal from "./components/AddClientModal";
import siteConfig from "../../config";
import { getclientlist } from "../../api/request/service";
import {
  ChangeClientStatusApi,
  DeleteClientApi,
  ExportClientsApi,
} from "../../api/request/clients/clients";
import LoadableButton from "../../components/button/LoadableButton";
import Export from "../../assets/Export";
import UserAvatar from "../../components/common/UserAvatar";
import Search from "../../components/common/Search";
import GlobalTooltip from "../../components/common/GlobalTooltip";

const Clients = () => {
  const navigate = useNavigate();
  const userDetails = useSelector((state) => state?.user?.userDetails);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [isStatusLoading, setIsStatusLoading] = useState(false);
  const [isClientDeleteModal, setIsClientDeleteModal] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isExportLoading, setIsExportLoading] = useState(false);

  const secretKey = process.env.REACT_APP_SECRET_KEY;

  const handleView = (data) => {
    navigate(`/client_details/${data?.id}`);
  };

  const getClientList = async (searchText) => {
    try {
      setIsLoading(true);
      const params = { page: currentPage, search: searchText };
      const { data } = await getclientlist(params);
      if (data?.status) {
        const clientData = data?.data?.map((item) => {
          return {
            ...item,
            userDetails: (
              <UserAvatar
                data={{
                  name: item?.name,
                  email: item?.email,
                  profile_image: item?.profile_image,
                }}
              />
            ),
            statusTag: (
              <div onClick={(e) => e.stopPropagation()}>
                <Switch
                  checked={item?.status === "active"}
                  onClick={() => handleClientStatus(item)}
                  checkedChildren="On"
                  unCheckedChildren="Off"
                />
              </div>
            ),
            paymentText: item?.payment ? (
              <p
                className={`${
                  item?.is_invoice_send && "font-bold capitalize"
                } ${
                  item?.payment === "Paid"
                    ? "text-green-500"
                    : item?.is_invoice_send && "text-red-600"
                }`}
              >
                {item?.is_invoice_send ? item?.payment : "--"}
              </p>
            ) : null,
            created_at: item?.created_at
              ? moment(item?.created_at).format("LL")
              : "",
            action: (
              <div
                className="flex items-center gap-3"
                onClick={(e) => e.stopPropagation()}
              >
                <GlobalTooltip title={"View Client Details"}>
                  <button onClick={() => handleView(item)}>
                    <View />
                  </button>
                </GlobalTooltip>
                <GlobalTooltip title={"Archive Client"}>
                  <button
                    onClick={() => {
                      setSelectedClient(item);
                      setIsClientDeleteModal(true);
                    }}
                  >
                    <Delete />
                  </button>
                </GlobalTooltip>
                <GlobalTooltip title={"Login Client"}>
                  {userDetails?.role === "admin" && (
                    <div
                      className="cursor-pointer"
                      onClick={() => handleClientLogin(item)}
                    >
                      <Login />
                    </div>
                  )}
                </GlobalTooltip>
              </div>
            ),
          };
        });
        setData(clientData);
        setTotal(data?.total_record);
        setIsLoading(false);
      }
    } catch (err) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getClientList();
  }, [currentPage]);

  const handleClientStatus = (item) => {
    setSelectedClient(item);
    setIsStatusModalOpen(true);
  };

  const updateClientStatus = (id, status) => {
    setData((prev) => {
      return prev.map((item) => {
        if (item?.id === id) {
          return {
            ...item,
            statusTag: (
              <div onClick={(e) => e.stopPropagation()}>
                <Switch
                  checked={status === "active"}
                  onClick={() => handleClientStatus({ ...item, status })}
                  checkedChildren="On"
                  unCheckedChildren="Off"
                />
              </div>
            ),
          };
        }
        return item;
      });
    });
  };

  const handleStatuChange = async () => {
    setIsStatusLoading(true);
    try {
      const status =
        selectedClient?.status === "active" ? "inactive" : "active";
      const params = { id: selectedClient?.id, status };
      const { data } = await ChangeClientStatusApi(params);
      if (data?.status) {
        toast.success(data?.message);
        setIsStatusModalOpen(false);
        updateClientStatus(selectedClient?.id, status);
      }
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
    } finally {
      setIsStatusLoading(false);
    }
  };

  const handleDeleteClient = async () => {
    try {
      setIsDeleteLoading(true);
      const { data } = await DeleteClientApi({ client_id: selectedClient?.id });
      if (data?.status) {
        toast.success(data?.message);
        getClientList();
        setIsClientDeleteModal(false);
      }
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const handleClientLogin = async (client) => {
    const cookies = new Cookies();
    const { token } = cookies.getAll();
    const data = JSON.stringify({ token, clientId: client?.id });
    const encrypted = CryptoJS.AES.encrypt(data, secretKey);
    const safeCiphertext = encrypted.toString();

    const redirectUrl = `${
      siteConfig?.clientUrl
    }/admin_login?token=${encodeURIComponent(safeCiphertext)}`;
    window.open(redirectUrl);
  };

  const exportClients = async () => {
    try {
      setIsExportLoading(true);
      const headers = { responseType: "arraybuffer" };
      const { data } = await ExportClientsApi({}, headers);
      const blob = new Blob([data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const timestamp = new Date().toISOString();
      saveAs(blob, `clients_${timestamp}.xlsx`);
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
    } finally {
      setIsExportLoading(false);
    }
  };

  const handleTableChange = (pagination) => {
    setCurrentPage(pagination?.current);
  };

  return (
    <div>
      <div className="border rounded-lg bg-White pb-5">
        <div className="sm:flex items-center justify-between p-5">
          <p className="title sm:mb-0 mb-3">
            Clients {total ? `(${total})` : null}
          </p>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <Search handleSearch={getClientList} />
            <div className="flex gap-3">
              <button
                className="btn-primary !px-4 flex items-center gap-2"
                onClick={() => setIsModalOpen(true)}
              >
                <Add />
                <span>Add Client</span>
              </button>

              <LoadableButton
                className="!px-4"
                lable={"Export"}
                onClick={exportClients}
                isLoading={isExportLoading}
                loadingLable="Exporting..."
              >
                <Export /> Export
              </LoadableButton>
            </div>
          </div>
        </div>

        <SkeletonTable
          columns={columns}
          data={data}
          isLoading={isLoading}
          pagination={{
            current: currentPage,
            total: total,
            pageSize: 10,
            showSizeChanger: false,
          }}
          handleTableChange={handleTableChange}
          onRowClick={(data) => handleView(data)}
        />
      </div>

      {/* Create client modal */}
      <AddClientModal
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        getClientList={getClientList}
      />

      {/* Confirmation modal for status */}
      <ConfirmationModal
        isOpen={isStatusModalOpen}
        setIsOpen={setIsStatusModalOpen}
        message="Are you sure, you want to change client status?"
        onConfirm={handleStatuChange}
        isLoading={isStatusLoading}
        loadingLabel="Changing status..."
      />

      {/* Delete Client */}
      <ConfirmationModal
        isOpen={isClientDeleteModal}
        setIsOpen={setIsClientDeleteModal}
        message="Are you sure, want to archive this client?"
        onConfirm={() => handleDeleteClient()}
        isLoading={isDeleteLoading}
        loadingLabel="Deleting Client..."
      />
    </div>
  );
};

export default Clients;
